import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { formatRelative, parseBackendDate, parseBackendJson } from "@/lib/format";
import {
  Activity, MessageSquare, UserCog, Bell, Filter, Pause, Play,
  ChevronRight, ChevronDown,
} from "lucide-react";
import { cn } from "@/lib/cn";

interface ActivityRow {
  id: string;
  business_id: string | null;
  contact_id: string | null;
  conversation_id: string | null;
  actor_type: string;
  event_type: string;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

const EVENT_GROUPS: { label: string; prefixes: string[] }[] = [
  { label: "AI",          prefixes: ["ai_"] },
  { label: "Channels",    prefixes: ["channel_", "webhook_", "waha_", "meta_"] },
  { label: "Properties",  prefixes: ["property_"] },
  { label: "Knowledge",   prefixes: ["knowledge_"] },
  { label: "Visits",      prefixes: ["visit_"] },
  { label: "Notifications", prefixes: ["notification_"] },
  { label: "Scoring",     prefixes: ["score_", "awareness_", "temperature_", "status_"] },
  { label: "Takeover",    prefixes: ["takeover_"] },
  { label: "Followups",   prefixes: ["followup_"] },
  { label: "Settings",    prefixes: ["settings_"] },
  { label: "Imports",     prefixes: ["contacts_imported", "properties_imported"] },
  { label: "Errors",      prefixes: ["error", "queue_failure"] },
];

function matchesGroup(eventType: string, group: typeof EVENT_GROUPS[number]): boolean {
  return group.prefixes.some((p) => eventType.startsWith(p) || eventType === p);
}

export function ActivityLogPage() {
  const [groupFilter, setGroupFilter] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [paused, setPaused] = useState(false);

  const { data: rows = [], dataUpdatedAt, isLoading } = useQuery({
    queryKey: ["activity-log"],
    queryFn: () => api.get<ActivityRow[]>("/api/activity?limit=200"),
    refetchInterval: paused ? false : 5_000,
  });

  // Filter client-side - the endpoint already returns 200 most recent.
  const visible = rows.filter((r) => {
    if (groupFilter) {
      const grp = EVENT_GROUPS.find((g) => g.label === groupFilter);
      if (grp && !matchesGroup(r.event_type, grp)) return false;
    }
    if (search.trim().length > 0) {
      const q = search.trim().toLowerCase();
      const hay = `${r.event_type} ${r.actor_type} ${JSON.stringify(r.metadata ?? {})}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Activity log</h1>
          <p className="text-sm text-slate-500">
            Real-time stream of system events. Refreshes every 5s.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-500">
            Last refresh: {formatRelative(new Date(dataUpdatedAt).toISOString())}
          </span>
          <Button
            variant={paused ? "primary" : "outline"}
            size="sm"
            onClick={() => setPaused((v) => !v)}
          >
            {paused ? (<><Play className="h-4 w-4" /> Resume</>) : (<><Pause className="h-4 w-4" /> Pause</>)}
          </Button>
        </div>
      </header>

      <Card className="p-3 flex flex-wrap items-center gap-2">
        <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-400">
          <Filter className="h-3 w-3" /> group
        </span>
        <button
          onClick={() => setGroupFilter(null)}
          className={cn(
            "px-2 py-0.5 rounded text-[11px] font-medium",
            groupFilter === null ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200",
          )}
        >
          all
        </button>
        {EVENT_GROUPS.map((g) => (
          <button
            key={g.label}
            onClick={() => setGroupFilter(g.label === groupFilter ? null : g.label)}
            className={cn(
              "px-2 py-0.5 rounded text-[11px] font-medium",
              groupFilter === g.label ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200",
            )}
          >
            {g.label}
          </button>
        ))}
        <Input
          placeholder="Search event / metadata…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="ml-auto max-w-xs"
        />
        <span className="text-[11px] text-slate-500">{visible.length} / {rows.length}</span>
      </Card>

      <Card>
        {visible.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400">
            {isLoading ? "Loading…" : "No events match."}
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {visible.map((e) => (
              <EventRow key={e.id} row={e} />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row component - collapsible metadata
// ---------------------------------------------------------------------------

function EventRow({ row }: { row: ActivityRow }) {
  const [open, setOpen] = useState(false);
  // The backend ships `payload` (aliased to `metadata`) as a JSON string;
  // parseBackendJson turns it into an object so we can introspect it.
  const meta = parseBackendJson<Record<string, unknown>>(row.metadata);
  const hasMeta = meta != null && typeof meta === "object" && Object.keys(meta).length > 0;
  return (
    <li className="px-4 py-2.5">
      <button
        type="button"
        onClick={() => hasMeta && setOpen((v) => !v)}
        className={cn(
          "w-full flex items-start gap-3 text-left",
          hasMeta && "cursor-pointer",
        )}
      >
        <ActorIcon actor={row.actor_type} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-900">{prettyEvent(row.event_type)}</span>
            <EventTypeBadge eventType={row.event_type} />
            {hasMeta && (
              open
                ? <ChevronDown className="h-3 w-3 text-slate-400" />
                : <ChevronRight className="h-3 w-3 text-slate-400" />
            )}
          </div>
          <div className="text-[11px] text-slate-500 flex flex-wrap items-center gap-2 mt-0.5">
            <span>{row.actor_type}</span>
            <span>·</span>
            <time className="tabular-nums">{formatAbsoluteCompact(row.created_at)}</time>
            <span className="text-slate-400">({formatRelative(row.created_at)})</span>
            {row.contact_id && (
              <span className="font-mono text-[10px] bg-slate-100 px-1.5 py-0.5 rounded">
                contact: …{row.contact_id.slice(-8)}
              </span>
            )}
            {row.conversation_id && (
              <span className="font-mono text-[10px] bg-slate-100 px-1.5 py-0.5 rounded">
                conv: …{row.conversation_id.slice(-8)}
              </span>
            )}
          </div>
        </div>
      </button>
      {open && hasMeta && (
        <pre className="mt-2 ml-9 rounded bg-slate-50 border border-slate-200 p-2 text-[10px] font-mono text-slate-700 overflow-x-auto whitespace-pre-wrap break-all">
          {JSON.stringify(meta, null, 2)}
        </pre>
      )}
    </li>
  );
}

function ActorIcon({ actor }: { actor: string }) {
  const cls = "h-7 w-7 rounded-full flex items-center justify-center text-white shrink-0";
  if (actor === "ai") return <span className={`${cls} bg-slate-900`} title="AI"><MessageSquare className="h-4 w-4" /></span>;
  if (actor === "human") return <span className={`${cls} bg-amber-500`} title="Human"><UserCog className="h-4 w-4" /></span>;
  return <span className={`${cls} bg-slate-300`} title="System"><Activity className="h-4 w-4" /></span>;
}

function EventTypeBadge({ eventType }: { eventType: string }) {
  // Pick a color hint based on the prefix.
  let bg = "bg-slate-100 text-slate-600";
  if (eventType.startsWith("ai_") || eventType.includes("invalid_output")) bg = "bg-blue-100 text-blue-800";
  if (eventType.startsWith("knowledge_")) bg = "bg-purple-100 text-purple-800";
  if (eventType.startsWith("property_")) bg = "bg-teal-100 text-teal-800";
  if (eventType.startsWith("visit_")) bg = "bg-pink-100 text-pink-800";
  if (eventType.startsWith("takeover_")) bg = "bg-amber-100 text-amber-800";
  if (eventType.startsWith("followup_")) bg = "bg-indigo-100 text-indigo-800";
  if (eventType.startsWith("notification_")) bg = "bg-rose-100 text-rose-800";
  if (eventType === "error" || eventType === "queue_failure" || eventType.endsWith("_failed")) bg = "bg-red-100 text-red-800";
  return (
    <span className={cn("text-[10px] font-mono px-1.5 py-0.5 rounded", bg)}>
      {eventType}
    </span>
  );
}

function prettyEvent(t: string): string {
  return t.replace(/_/g, " ");
}

function formatAbsoluteCompact(iso: string): string {
  try {
    const d = parseBackendDate(iso);
    return d.toLocaleString("en-GB", {
      day: "2-digit", month: "short",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch {
    return iso;
  }
}

// keep these imports referenced even if RT doesn't need them inline
void Bell;
